/**
 * Sequencia de arranque.
 *
 * Orçamento: primeira tela em menos de 2 s num Android modesto, menos de 1 s
 * offline. O que esta aqui e o caminho critico; tudo o resto e adiado.
 *
 * A ordem NAO e arbitraria:
 *
 *  1. Sessao primeiro (localStorage, sincrono) — o registro de plugins filtra
 *     rotas e widgets por permissoes, e precisa de saber quem esta a usar isto.
 *  2. Descobrir e registrar plugins ANTES de abrir a base de dados: o IndexedDB
 *     nao aceita tabelas novas depois de aberto, e as tabelas vem dos manifestos.
 *     (`createCore` faz este par pela ordem certa.)
 *  3. `register()` de cada plugin, com falha suave. Um plugin partido e uma
 *     linha no diagnostico — nunca uma tela branca. O lembrete da medicacao tem
 *     de aparecer mesmo que as financas estejam avariadas.
 *  4. `CoreEvents.READY`.
 *  5. Sincronizacao, notificacoes e assistente: DEPOIS, fora do caminho critico.
 */
import { CoreEvents, type FlosPlugin } from '@flos/shared';
import { createCore, type FlosCore } from '@flos/core';
import {
  discoverPlugins,
  loadAiModule,
  loadAuthModule,
  loadNotificationsModule,
  loadSyncModule,
  type DiscoveryProblem,
} from './wiring/modules';
import { startAuth, unavailableAuth } from './wiring/auth';
import { DORMANT_MESSAGES, buildSyncEngine } from './wiring/syncEngine';
import { decideSession, permissionsFor, readStoredSession } from './wiring/sessionStore';
import { readDisabledPlugins } from './wiring/preferences';
import { createSyncController, type SyncController } from './wiring/syncController';
import {
  AI_KEY_SETTING,
  AI_SETTINGS_NAMESPACE,
  createAssistant,
  unavailableAssistant,
  type Assistant,
} from './wiring/assistant';
import type {
  AuthControllerLike,
  IntentRouterLike,
  ReadOnlyState,
  RestoredSession,
} from './wiring/ports';

export interface BootOptions {
  /** Substitui a descoberta automatica. Usado nos testes. */
  plugins?: readonly FlosPlugin[];
  databaseName?: string;
  /** Substitui a sessao salva. Usado nos testes e no fim do onboarding. */
  session?: RestoredSession | null;
  /** Injectavel: `navigator.onLine` nao existe em ambiente de teste puro. */
  online?: boolean;
  /** Por padrão ligamos o motor de sincronizacao depois do primeiro pintado. */
  startSync?: boolean;
  now?: () => Date;
  /** Router de intencoes injectado — os testes usam-no para nao chamar modelo nenhum. */
  assistantRouter?: IntentRouterLike;
  /**
   * Identificador publico do cliente OAuth. Por omissao vem do `.env`.
   * Nao e segredo: o que protege a conta e a origem autorizada (docs/SECURITY.md).
   */
  googleClientId?: string | undefined;
}

export interface FailedPlugin {
  id: string;
  name: string;
  reason: string;
}

export interface BootResult {
  core: FlosCore;
  sync: SyncController;
  assistant: Assistant;
  /**
   * A conta. Nasce indisponivel e e substituida na fase diferida — como o
   * assistente, e por isso um getter: quem le no primeiro pintado recebe o
   * controlador de reserva, que sabe dizer em portugues porque nao ha entrada.
   */
  auth: AuthControllerLike;
  /** Nao-nulo quando o aplicativo esta em leitura-apenas, com o motivo em portugues. */
  readOnly: ReadOnlyState | null;
  /** `true` quando nao ha sessao utilizavel e e preciso passar pela entrada. */
  needsOnboarding: boolean;
  /** Plugins recusados, falhados ou ignorados — mostrados no diagnostico. */
  failedPlugins: FailedPlugin[];
  /** Milissegundos ate o nucleo estar pronto. Alimenta o KPI de arranque. */
  elapsedMs: number;
  destroy(): Promise<void>;
}

function detectOnline(explicit: boolean | undefined): boolean {
  if (typeof explicit === 'boolean') return explicit;
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

export async function boot(options: BootOptions = {}): Promise<BootResult> {
  const startedAt = Date.now();
  const online = detectOnline(options.online);

  // 1. Sessao. Sincrono e barato: nao vale a pena esperar por rede nenhuma.
  const stored = options.session !== undefined ? options.session : readStoredSession();
  const decision = decideSession({
    stored,
    online,
    ...(options.now ? { now: options.now() } : {}),
  });

  // 2. Plugins. A descoberta e paralela e nao lanca: os problemas viram relatorio.
  const discovery = options.plugins
    ? { plugins: [...options.plugins], problems: [] as DiscoveryProblem[] }
    : await discoverPlugins();

  // Plugins desligados nas configuracoes nem chegam a declarar entidades.
  const disabled = new Set(readDisabledPlugins());
  const enabled = discovery.plugins.filter((plugin) => !disabled.has(plugin.manifest.id));

  // 3. Nucleo: entidades registradas, base aberta, plugins arrancados com falha suave.
  const core = await createCore({
    plugins: enabled,
    ...(options.databaseName ? { databaseName: options.databaseName } : {}),
    ...(decision.session
      ? {
          session: {
            info: decision.session.info,
            permissions: permissionsFor(decision.session),
          },
        }
      : {}),
    autoStart: false,
  });

  // 4. `start()` emite `CoreEvents.READY` no fim. Nunca lanca por causa de um
  //    plugin: o registro pega cada `register()` dentro do seu try/catch.
  const report = await core.start();
  const elapsedMs = Date.now() - startedAt;

  const failedPlugins: FailedPlugin[] = [
    ...discovery.problems.map((problem) => ({
      id: problem.source,
      name: problem.source,
      reason: problem.reason,
    })),
    ...core.registry
      .all()
      .filter((entry) => entry.state === 'failed' || entry.state === 'rejected' || entry.state === 'skipped')
      .map((entry) => ({
        id: entry.manifest.id,
        name: entry.manifest.name || entry.manifest.id,
        reason: entry.problems.join('; ') || 'motivo desconhecido',
      })),
  ];

  if (failedPlugins.length > 0) {
    core.logger.warn('Arranque concluido com plugins de fora', {
      total: failedPlugins.length,
      plugins: failedPlugins.map((p) => p.id),
    });
  }
  core.logger.info('Aplicativo pronta', {
    ms: elapsedMs,
    carregados: report.loaded.length,
    online,
  });

  // 5. Tudo o que se segue fica FORA do caminho critico. Se falhar, o aplicativo
  //    ja esta a funcionar e a pessoa ja consegue ver e escrever dados locais.
  const sync = createSyncController(core, null);
  let assistant: Assistant = unavailableAssistant(
    'O assistente está se preparando. Um momento.',
  );
  let auth: AuthControllerLike = unavailableAuth(
    'A entrada com a sua conta está se preparando. Um momento.',
  );

  const deferred = async (): Promise<void> => {
    const [aiModule, authModule, syncModule, notificationsModule] = await Promise.all([
      loadAiModule(),
      loadAuthModule(),
      loadSyncModule(),
      loadNotificationsModule(),
    ]);

    const apiKey = core.settings.getSync<string>(AI_SETTINGS_NAMESPACE, AI_KEY_SETTING, '');
    assistant = createAssistant({
      core,
      module: aiModule,
      apiKey: apiKey || undefined,
      ...(options.assistantRouter ? { router: options.assistantRouter } : {}),
    });

    // A conta antes do motor: sem forma de obter um token, `buildSyncEngine`
    // fica dormente por `sem-conta` em vez de encher a fila de tentativas
    // recusadas ate as parquear.
    auth = await startAuth({
      core,
      module: authModule,
      clientId:
        options.googleClientId ??
        (import.meta.env?.['VITE_GOOGLE_CLIENT_ID'] as string | undefined),
    });

    const built = await buildSyncEngine({ core, module: syncModule, auth });
    // Dormente nao e avaria: e o estado normal de uma casa que ainda nao acabou
    // a instalacao. O que nao pode acontecer e ela nao saber porque — por isso
    // o motivo viaja com o motor ate o cabecalho.
    sync.attach(built.engine, built.dormant ? DORMANT_MESSAGES[built.dormant] : null);
    if (built.dormant) {
      core.logger.info('Sincronizacao dormente; a fila fica salva localmente', {
        motivo: built.dormant,
      });
    }

    if (notificationsModule) {
      // Ate aqui os plugins tinham o notificador de reserva, que so escreve no
      // diagnostico. A partir de agora um lembrete de medicacao chega mesmo.
      try {
        core.useNotifier(notificationsModule.createNotifier({ logger: core.logger }));
      } catch (error) {
        // Falhar a ligar os avisos nao pode tirar-lhe o resto: sem isto, ela
        // continua a ver os medicamentos ao abrir — so nao e avisada sozinha.
        core.logger.warn('Nao consegui ligar o servico de notificacoes', {
          erro: error instanceof Error ? error.message : String(error),
        });
      }
    } else {
      core.logger.info('Sem pacote de notificacoes; os lembretes so aparecem com o app aberto');
    }

    await sync.refresh();
  };

  const schedule = (task: () => void): void => {
    // `requestIdleCallback` nao existe no Safari do iPad.
    const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
      .requestIdleCallback;
    if (typeof idle === 'function') idle(task);
    else setTimeout(task, 0);
  };

  if (options.startSync === false) {
    await deferred();
  } else {
    schedule(() => {
      void deferred().catch((error: unknown) => {
        core.logger.error('Arranque diferido falhou', {
          erro: error instanceof Error ? error.message : String(error),
        });
      });
    });
  }

  return {
    core,
    sync,
    get assistant() {
      return assistant;
    },
    get auth() {
      return auth;
    },
    readOnly: decision.readOnly,
    needsOnboarding: decision.needsSignIn,
    failedPlugins,
    elapsedMs,
    async destroy() {
      sync.stop();
      // Antes de fechar o nucleo: `stop()` larga os ouvintes do fornecedor de
      // identidade. Sem isto, um `boot()` seguido de outro — que e o que os
      // testes fazem — deixava o anterior a publicar para uma base ja fechada.
      auth.stop();
      core.events.emit(CoreEvents.SESSION_CHANGED, { previous: null, current: null });
      await core.destroy();
    },
  };
}
